"use client";

import { getExcelReadFirstColumn } from "@/app/helpers/excel";
import { Check, ChevronLeft, ChevronRight, FileUp, Pencil, Plus, Search, Trash2, X, } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/app/context/UserContext";
import { AlertType } from "@/app/constants/enums";
import { ValidationOption } from "@/app/models/interfaceProject";

interface Props {
  value: string[];  
  onChange: (list: string[]) => void;
  onSave: (list: string[]) => void; 
  onClose: () => void; 
}

export default function PermissionsList({
  value,  
  onChange,
  onSave,
  onClose
}: Props) {
    const { userProject, setUserProject, addAlert} = useUser();
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    const [viewAdd, setViewAdd] = useState(false);
    const [newValue, setNewValue] = useState("");

    const [editValue, setEditValue] = useState("");
    const [editRow, setEditRow] = useState("");
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    /* ================= FILTER + PAGINATION ================= */

    const filteredData = useMemo(
        () =>
            value.filter((v) =>
            v.toLowerCase().includes(search.trim().toLowerCase())
            ),
        [value, search]
    );

    const paginatedData = filteredData.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    useEffect(() => {
        setPage(1);
    }, [search, value.length]);

    /* ================= ACTIONS ================= */

    const handleAdd = () => {
        if (!newValue.trim()){
            addAlert(`Ingrese permiso`,AlertType.Warning) 
            return; 
        }
        if (value.includes(newValue.trim())) {
            addAlert(`El permiso ya existe`,AlertType.Warning) 
            return;
        } 
        onChange([newValue.trim(), ...value]);
        setNewValue("");
        setViewAdd(false);
    };

    const handleEdit = (newValue: string) => {
        if (!newValue.trim()) return;
        const updated = value.map((v) =>
            v === editRow ? newValue.trim() : v
        ); 
        onChange(updated);
        setEditValue("");
        setEditRow(""); 
    };

    const handleDelete = (item: string) => {
        const updated = value.filter(v => v !== item);
        onChange(updated);
    };

    const handleExcelUpload = async (file: File) => {
        try {
            const excelValues = await getExcelReadFirstColumn(file); 
            const toAdd = excelValues
            .map(v => v?.toString().trim())
            .filter(v => v)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .filter(v => !value.includes(v));
            if (toAdd.length === 0) return; 
            onChange([...toAdd,...value]);
            setPage(1);
        } catch { 
            addAlert(`Error al leer el archivo Excel`,AlertType.Error) 
        }
    };

    /* ================= RENDER ================= */
    const toggleSelect = (select: string) => {
        setSelectedItems((prev) =>
            prev.includes(select)
            ? prev.filter((i) => i !== select)
            : [...prev, select]
        );
    }; 
    const toggleSelectAll = () => { 
         
    };


  return (
    <div className="w-full animate-fade-in"> 
      <h3 className="text-lg font-bold py-4 px-5">Listado de permisos</h3>
        <div className="overflow-auto px-8 py-2
                      min-w-[400px] max-w-[600px] 
                      sm:min-w-[600px] sm:max-w-[100%] 
                      md:min-w-[700px] md:max-w-[799px] 
                      lg:min-w-[800px] lg:max-w-[800px]

                      min-h-[100px] max-h-[200px] 
                      sm:min-h-[200px] sm:max-h-[100%s] 
                      md:min-h-[250px] md:max-h-[300px] 
                      lg:min-h-[300px] lg:max-h-[100%]">
            <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Buscar permiso..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border rounded n-fmr-input-search border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
                    />
                        <Search className="absolute right-2.5 top-3.5 text-gray-400" size={18} />
                </div>
                <button
                    onClick={() => fileInputRef.current?.click()}                
                    className={`flex items-center btn-inca-secondary justify-center px-2 py-2 w-10 h-10 rounded-full bg-gray-100 transition-colors border shadow-md  hover:bg-gray-200 `}
                >
                    <FileUp size={20} />
                </button>
                <button
                    onClick={() => setViewAdd(true)} 
                    className={`flex items-center btn-inca-secondary justify-center px-2 py-2 w-10 h-10 rounded-full bg-gray-100 transition-colors border shadow-md  hover:bg-gray-200 `}
                >
                    <Plus size={20} />
                </button> 
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) =>
                        e.target.files && handleExcelUpload(e.target.files[0])
                    }
                />            
        </div>
        {viewAdd && (
        <div className="flex items-center gap-2 mb-4 animate-fade-in">
            <input
                type="text"
                placeholder="Nuevo permiso"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="w-full border n-fmr-input my-2 px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700" 
            />
            <button
                onClick={handleAdd}
                className={`flex items-center text-white justify-center px-2 py-2 rounded-full bg-blue-600 transition-colors border shadow-md hover:bg-blue-800`}
            >
                <Check className="w-5 h-5 animate-fade-in" strokeWidth={1.5} /> 
            </button>
            <button
                onClick={() => setViewAdd(false)}
                className="flex items-center btn-inca-secondary justify-center px-2 py-2 rounded-full transition-colors border shadow-md"
            >
                <X className="w-5 h-5 animate-fade-in" strokeWidth={1.5} /> 
            </button>
        </div>
        )}
 
        {paginatedData.map((item) => { 
            return (
            <div
                key={item}
                className="flex justify-between items-center border-b text-sm animate-fade-in pb-2 pt-2 "
            >
                {item === editRow ? (
                    <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full border n-fmr-input  border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700 mr-2" 
                    />
                ) : (
                    <span className="flex-1 font-medium m-2">{item}</span>
                )}
                <div className="flex gap-2">
                    <button
                        onClick={() =>
                            item === editRow
                            ? handleEdit(editValue)
                            : (setEditRow(item), setEditValue(item))
                        }
                        className={`flex items-center text-white justify-center px-2 py-2 rounded-full bg-blue-600 transition-colors border shadow-md hover:bg-blue-800`}
                    >                        
                        {item === editRow ? 
                            <Check className="w-4 h-4" strokeWidth={1.5} /> : 
                            <Pencil className="w-4 h-4" strokeWidth={1.5} /> } 
                    </button> 
                    <button
                        onClick={() => handleDelete(item)}
                        className={`flex items-center btn-inca-secondary justify-center px-2 py-2 rounded-full transition-colors border shadow-md`}
                    >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} /> 
                    </button>
                </div>
                
            </div>
            );
        })}

            
        {filteredData.length > itemsPerPage && (
        <div className="flex justify-end items-center mt-2">
            <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 mr-2 shadow-lg 
                    ${page > 1 ? "hover:shadow-xl hover:bg-white hover:border-blue-400  ":" text-slate-400"}
                    `}
            >
                <ChevronLeft />
            </button>

            <span className="p-2 pt-2 pb-2 bg-blue-700 text-white rounded-full disabled:opacity-50 mr-2">
                {page} / {Math.ceil(filteredData.length / itemsPerPage)}
            </span>

            <button
                onClick={() =>
                setPage((p) =>
                    p * itemsPerPage < filteredData.length ? p + 1 : p
                )
                }
                disabled={page * itemsPerPage >= filteredData.length}
                className={`p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 mr-2 shadow-lg 
                      ${(page * itemsPerPage >= filteredData.length) ? "text-slate-400": "hover:shadow-xl hover:bg-white hover:border-blue-400 "}`}
            >
                <ChevronRight />
            </button>
        </div>
        )} 

    </div>

        <div className="flex justify-end gap-2 px-5 py-5 pr-8">
            <button 
                type="button" 
                onClick={() => onClose()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700  py-2 px-4 mr-2 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
            >
                Cancelar
            </button> 
            <button 
                type="button" 
                onClick={() => onSave(value)}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
            >
                Agregar permisos
            </button>
        </div> 
     
    </div>
  );
}
